
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { debounce } from 'lodash';
import { ValidationProvider } from 'vee-validate';

import { sharedState } from '../../framework/state';
import { getResourcesNew } from '../../framework/client/resource';
import { getDatabaseOptions, getEnumerationOptions, OptionValue } from '../../framework/service/options';
import { getFieldNameFromVModelProperty } from '../../framework/util/component_util';

import { ApplicationResource } from '../../../common/application/enumeration/ApplicationResource';
import { CustomerVehicleState, VehicleState } from '../../../common/application/model/enums/VehicleState';
import { sortAlphabetically } from '../service/helper_service';

@Component({
    components: {
        Multiselect,
        ValidationProvider,
    },
})
export default class AutoCompleteMultipleField extends Vue {
    $refs!: {
        multiselect: Multiselect;
    };

    @Prop(Boolean) readonly disabled!: string;
    @Prop(Boolean) readonly expanded!: boolean;
    @Prop(String) readonly searchField!: string;
    @Prop(String) readonly idField!: string;
    @Prop(Boolean) readonly multiple!: boolean;
    @Prop(String) readonly name!: string;
    @Prop(String) readonly resource!: ApplicationResource | 'invoice-row-type' | 'vehicle-state';
    @Prop(Boolean) readonly required!: boolean;
    @Prop(Array) readonly value!: string[];
    @Prop(Map) readonly parameters!: Map<string, string>;
    @Prop(Boolean) readonly taggable!: boolean;
    @Prop(Boolean) readonly isAdmin?: boolean;

    shared = sharedState;

    local = {
        filteredObjects: [] as OptionValue[],
        selectedResources: [] as OptionValue[],
        pageSize: 1000,
        isLoading: false,
        isTouched: false,
    };

    async mounted() {
        await this.asyncFind('');
        await this.modelValueChange(this.value);
    }

    @Watch('value')
    async modelValueChange(newValue: string[]) {
        this.local.selectedResources = this.local.selectedResources ?? [];
        this.local.filteredObjects = this.local.filteredObjects ?? [];

        if (!newValue) {
            this.local.selectedResources = [];
            return;
        }

        newValue.forEach((resourceId) => {
            const newResource = this.local.filteredObjects.find(({ id }) => resourceId === id);
            const oldResource = this.local.selectedResources.find(({ id }) => resourceId === id);

            if (newResource && !oldResource) {
                this.local.selectedResources.push(newResource);
            }
        });
    }

    @Watch('parameters')
    async queryValueChange() {
        await this.asyncFind('');
    }

    dropdownClosed(opened: boolean) {
        this.local.isTouched = true;

        if (opened) {
            return;
        }

        let value: string[];

        if (this.multiple) {
            value = this.local.selectedResources.map(({ id }) => id!!);
        } else {
            const selectedResource = this.local.selectedResources as any;
            value = selectedResource && selectedResource.id ? [selectedResource.id] : [];
        }

        this.$emit('dropdown-closed', value, this.idField);
    }

    deBouncedSearch = debounce((searchString: string) => {
        this.asyncFind(searchString);
    }, 500);

    clearAll(): void {
        this.local.selectedResources = [];

        if (!this.multiple) {
            (this.$refs.multiselect as any).deactivate();
        }
    }

    selectAll(): void {
        this.local.selectedResources = [...this.local.filteredObjects];
    }

    getFieldName(): string {
        return getFieldNameFromVModelProperty(this);
    }

    addTag(tagName: any) {
        this.$emit('tag', tagName);
    }

    isInvalid() {
        return this.required && this.local.isTouched && this.local.selectedResources.length === 0;
    }

    private async asyncFind(query: string): Promise<void> {
        this.local.isLoading = true;
        const parameters = new Map<string, any>();
        parameters.set('pageSize', this.local.pageSize);

        if (query && query.length) {
            parameters.set(this.searchField, `ILIKE_%${query}%`);
        }

        if (this.parameters) {
            for (const key of this.parameters.keys()) {
                parameters.set(key, this.parameters.get(key));
            }
        }

        if (!sharedState.admin) {
            if (!sharedState.context.scopes.manager.customerId) {
                this.local.filteredObjects = [];
                this.local.isLoading = false;
                return;
            }

            if (this.resource !== ApplicationResource.VENDOR) {
                const key = this.resource === ApplicationResource.CUSTOMER ? 'id' : 'customerId';
                parameters.set(key, sharedState.context.scopes.manager.customerId);
            }
        }

        if (this.resource === 'vehicle-state') {
            this.local.filteredObjects = getEnumerationOptions(
                'VehicleState',
                Object.keys(this.isAdmin ? VehicleState : CustomerVehicleState),
                this,
            );
            this.local.filteredObjects.shift();
        } else if (this.resource === 'invoice-row-type') {
            const objects = await getDatabaseOptions('InvoiceRowType', this);
            this.local.filteredObjects = objects.filter((object) =>
                object.label.toLowerCase().includes(query.toLowerCase()),
            );
        } else {
            this.local.filteredObjects = await this.getOptions(this.resource, this.searchField, parameters);
        }
        this.local.isLoading = false;
    }

    private async getOptions(resource: string, field: string, parameters: Map<string, any>) {
        return (await getResourcesNew<any>(resource, -1, parameters))
            .map((o) => {
                return { id: o.id, label: o[field] };
            })
            .sort(sortAlphabetically('label'));
    }
}
